import { roomData } from '../data';
import { matrixClient } from '../../client';
import { Log } from '@fanfanlo';
import { ClientEvent, RoomStateEvent, Room, EventType, SyncState, RoomEvent } from 'matrix-js-sdk';
import type { IRoomSettings, IRoomPermissions, IRoomPrivacy } from '../types';

const fileLogger = new Log(false, 'im.client.room.handlers.RoomSettingsHandler');
fileLogger.pause = true;
fileLogger.childrenPaused = true;

/**
 * 房间设置部分字段（用于增量更新）
 */
export interface IRoomSettingsPartial {
  name?: string;
  topic?: string;
  avatarUrl?: string | null;
  permissions?: IRoomPermissions;
  privacy?: IRoomPrivacy;
}

const INTERESTED_EVENTS = [
  'm.room.name',
  'm.room.topic',
  'm.room.power_levels',
  'm.room.encryption',
  'm.room.guest_access',
  // ...如有其它关心的事件类型可补充
];

function getDefaultPrivacy(): IRoomPrivacy {
  return {
    isPublic: false,
    isEncrypted: false,
    allowGuestJoin: false
  };
}

function handleRoomCreated(room: Room) {
    // 只在异常时打印日志
    const logger = fileLogger.sub(false, 'handleRoomCreated');
    // logger.pause = true;
    logger.log('room=', room);
    const roomId = room.roomId;
    if (!room) {
        logger.warn('Room not found:', roomId);
        return;
    }
    // 可选：初始化时直接用 room 对象填充部分设置
    roomData.roomSettings.settings[roomId] = {
        ...roomData.roomSettings.settings[roomId],
        name: room.name,
        topic: roomData.roomSettings.settings[roomId]?.topic || '',
        avatarUrl: room.getAvatarUrl?.(matrixClient.client.baseUrl, 96, 96, 'crop') || null,
        permissions: roomData.roomSettings.settings[roomId]?.permissions || {
          sendMessages: 0, invite: 50, kick: 50, ban: 50, roomSettings: 50
        },
        privacy: roomData.roomSettings.settings[roomId]?.privacy || getDefaultPrivacy()
    };
}

function handleRoomDeleted(roomId: string) {
    // 只在关键流程打印日志
    const logger = fileLogger.sub(false, 'handleRoomDeleted');
    logger.log('delete roomId=', roomId);
    delete roomData.roomSettings.settings[roomId];
    delete roomData.roomSettings.isLoading[roomId];
}

function handleRoomNameChanged(roomId: string, name: string) {
    const logger = fileLogger.sub(false, 'handleRoomNameChanged');
    if (!roomData.roomSettings.settings[roomId]) {
        logger.warn('handleRoomNameChanged: room not found', roomId);
        return;
    }
    logger.log('handleRoomNameChanged:', roomId, 'name:', name);
    roomData.roomSettings.settings[roomId].name = name ?? '';
}

function handlePowerLevelsChanged(roomId: string, powerLevels: any) {
    const logger = fileLogger.sub(false, 'handlePowerLevelsChanged');
    logger.log('handlePowerLevelsChanged:', roomId, powerLevels);
    roomData.powerLevels[roomId] = powerLevels;
}

function onRoomTimeline(event: any, room: Room | undefined, toStartOfTimeline?: boolean) {
    const logger = fileLogger.sub(false, 'onRoomTimeline');
    logger.pause = true;
    logger.log('evnet=', event);
    logger.log('room=', room);
    logger.log('toStartOfTimeline=', toStartOfTimeline);
    if (!room || toStartOfTimeline) return;
    const roomId = room.roomId;
    const eventType = event.getType?.() || event.type;
    logger.log('RoomEvent.Timeline 触发:', roomId, 'eventType:', eventType, 'event:', event);
    if (eventType === 'm.room.name') {
        const name = event.getContent?.().name || event.content?.name || '';
        logger.log('RoomEvent.Timeline 检测到房间名变更:', roomId, 'name:', name);
        if (!roomData.roomSettings.settings[roomId]) {
            roomData.roomSettings.settings[roomId] = {
                name: '',
                topic: '',
                avatarUrl: null,
                permissions: {
                    sendMessages: 0, invite: 50, kick: 50, ban: 50, roomSettings: 50
                },
                privacy: getDefaultPrivacy()
            };
        }
        roomData.roomSettings.settings[roomId].name = name;
    }
    if (eventType === 'm.room.topic') {
        const topic = event.getContent?.().topic || event.content?.topic || '';
        logger.log('RoomEvent.Timeline 检测到房间简介变更:', roomId, 'topic:', topic);
        if (!roomData.roomSettings.settings[roomId]) {
            roomData.roomSettings.settings[roomId] = {
                name: '',
                topic: '',
                avatarUrl: null,
                permissions: {
                    sendMessages: 0, invite: 50, kick: 50, ban: 50, roomSettings: 50
                },
                privacy: getDefaultPrivacy()
            };
        }
        roomData.roomSettings.settings[roomId].topic = topic;
    }
    if (eventType === 'm.room.power_levels') {
        const powerLevels = event.getContent?.() || event.content || {};
        handlePowerLevelsChanged(roomId, powerLevels);
    }
}

// function handleSync(state: SyncState, prevState: SyncState | null, data: any) {
//     const logger = fileLogger.sub(false, 'handleSync');
//     logger.pause = true;
//     logger.log('handleSync: data=', data);
//     logger.log('state=', state);
//     logger.log('prevState=', prevState);
//     if(!data?.rooms?.join) return;
//     for (const roomId in data.rooms.join) {
//         const room = data.rooms.join[roomId];
//         for (const event of room.timeline.events) {
//             if (event.type === EventType.RoomName) {
//                 logger.log('handleSync: RoomName event', roomId, event.content?.name);
//                 handleRoomNameChanged(roomId, event.content?.name);
//             }
//             // 其它房间设置事件可在此解析并调用对应 handler
//         }
//     }
// }

export const RoomSettingsHandler = {
    init() {
        matrixClient.clientOnEventDispatcher.on(ClientEvent.Room, handleRoomCreated);
        matrixClient.clientOnEventDispatcher.on(ClientEvent.DeleteRoom, handleRoomDeleted);
        matrixClient.clientOnEventDispatcher.addListener(RoomEvent.Timeline, onRoomTimeline);
    }
}; 